import { IAureliaLoader } from '@desygner/ui-core-aurelia-loader';
import { ICredentialsManager } from '../../../../../../../core/viewer/credentials-manager';

import type { ConfigState, ShareModalAdapter } from './AureliaShareModal.types';

export class AureliaShareModalService {
  private containerElement: Element | null = null;
  private shareModalAdapter: ShareModalAdapter | null = null;
  private isLoaded = false;

  constructor(
    private readonly aureliaLoader: IAureliaLoader,
    private readonly credentialsManager: ICredentialsManager
  ) {}

  async initialize(configState: ConfigState): Promise<void> {
    if (this.containerElement) {
      return;
    }

    this.containerElement = this.getModalContainer();

    this.credentialsManager.loadUserToken();
    const { token: apiKey } = this.credentialsManager.getUserTokenData();

    const apiUrl = configState.api?.apiUrl || '';
    const viewerUrl = configState.resources?.viewerUrl || window.location.origin;
    const companyName = configState.brand?.name || '';
    const companyId = configState.brand?.companyId || '';

    (window as any).CONFIG = (window as any).CONFIG || {};

    (window as any).CONFIG.viewer_url = viewerUrl;
    (window as any).CONFIG.api_url = apiUrl;
    (window as any).CONFIG.base_url = apiUrl;
    (window as any).CONFIG.desygner_api = apiUrl;
    (window as any).CONFIG.base_host = window.location.host;
    (window as any).CONFIG.company_id = companyId;
    (window as any).CONFIG.company_name = companyName;
    (window as any).CONFIG.business = companyName;
    (window as any).CONFIG.user_token = apiKey;
    (window as any).CONFIG.api_key = apiKey;
    (window as any).CONFIG.environment = configState.environment || 'prod';
    (window as any).CONFIG.user = (window as any).CONFIG.user || {};
    (window as any).CONFIG.addons = (window as any).CONFIG.addons || {};
    (window as any).CONFIG.addons.pdf_viewer = true;
    
    (window as any).CONFIG.white_label_configuration = configState.whiteLabelConfiguration || {};
    (window as any).CONFIG.is_industry = !!configState.whiteLabelConfiguration;

    // Set up INKIVE.C for compatibility with legacy code
    (window as any).INKIVE = (window as any).INKIVE || {};
    (window as any).INKIVE.EditorFacade = (window as any).INKIVE.EditorFacade || {};
    (window as any).INKIVE.DesignFacade = (window as any).INKIVE.DesignFacade || {};
    (window as any).INKIVE.C = {
      api_url: apiUrl,
      viewer_url: viewerUrl,
      company_id: companyId,
      company_name: companyName,
      user_token: apiKey,
      api_key: apiKey,
      USER_API_URL: {
        token: apiKey,
        url: apiUrl,
      },
      WHITE_LABEL_CONFIGURATION: configState.whiteLabelConfiguration || {},
    };

    await this.aureliaLoader.loadAureliaLibraries(this.containerElement);

    if (!this.containerElement) {
      console.error('Container element became null');
      return;
    }

    this.containerElement.setAttribute('data-status', 'loaded');

    // Poll for the Aurelia viewModel to become available
    await this.waitForAureliaViewModel();
  }

  private getModalContainer(): Element {
    const element = document.querySelector('[aurelia-app="share-modal/main"]');

    if (element) return element;

    const div = document.createElement('div');

    div.setAttribute('aurelia-app', 'share-modal/main');
    div.setAttribute('data-status', 'loading');
    document.body.appendChild(div);

    return div;
  }

  private async waitForAureliaViewModel(): Promise<void> {
    return new Promise((resolve, reject) => {
      let pollAttempts = 0;
      const maxPollingAttempts = 50;
      const pollingInterval = 100;

      const intervalId = setInterval(() => {
        if (!this.containerElement) {
          clearInterval(intervalId);
          reject(new Error('Container element is null'));
          return;
        }

        const element = this.containerElement as any;
        const aureliaInstance = element.au || element.__au__ || element.aurelia;
        const rootViewModel = aureliaInstance?.root?.viewModel;

        if (rootViewModel?.toggle) {
          clearInterval(intervalId);
          this.shareModalAdapter = rootViewModel;
          this.isLoaded = true;
          resolve();
        } else if (pollAttempts > maxPollingAttempts) {
          clearInterval(intervalId);
          reject(new Error('Aurelia share modal failed to load within timeout'));
        }

        pollAttempts++;
      }, pollingInterval);
    });
  }

  isReady(): boolean {
    return this.isLoaded && this.shareModalAdapter !== null;
  }

  openShareModal(options: {
    design: unknown;
    previewImage: string;
    plan: null;
    linkOptionTypes?: string[];
    closeCallback: () => void;
  }): void {
    if (!this.isReady()) {
      console.error('Aurelia adapter not ready');
      return;
    }

    if (this.shareModalAdapter !== null) {
      this.shareModalAdapter.toggle(options);
    }
  }
}
